import { useState } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import LockResetIcon from "@mui/icons-material/LockReset";
import Typography from "@mui/material/Typography";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, storeATokenD } from "../../queries/api";
import BACKGROUND_IMAGE from "../../imgs/BRIDGES_SIGNIN_BACKGROUND.png";

export default function ResetPassword() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");
	const [error, setError] = useState("");
	const [done, setDone] = useState(false);
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError("");
		const form = new FormData(event.currentTarget);
		const password = form.get("password") as string;
		const confirm = form.get("confirm") as string;
		if (!password || !confirm) {
			setError("Please fill in both password fields");
			return;
		}
		if (password.length < 8) {
			setError("Password must be at least 8 characters");
			return;
		}
		if (password !== confirm) {
			setError("Passwords do not match");
			return;
		}
		if (!token) {
			setError("Reset link is invalid or has expired. Request a new one.");
			return;
		}
		setLoading(true);
		try {
			// The reset link carries a short-lived JWT; use it as the bearer token
			// so /password/change (which skips the old-password check when none is
			// supplied) sets the new password for that user.
			storeATokenD(token);
			await API.post("password/change", { password });
			setDone(true);
		} catch (e: any) {
			if (e?.response?.status === 401) {
				setError("Reset link has expired. Please request a new one.");
			} else {
				setError("Could not reset the password. Please try again.");
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<Grid container component="main" sx={{ height: "100vh" }}>
			<CssBaseline />
			<Grid
				item
				xs={false}
				sm={4}
				md={7}
				sx={{
					backgroundImage: `url(${BACKGROUND_IMAGE})`,
					backgroundRepeat: "no-repeat",
					backgroundColor: (t) => (t.palette.mode === "light" ? t.palette.grey[50] : t.palette.grey[900]),
					backgroundSize: "cover",
					backgroundPosition: "center",
				}}
			/>
			<Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square sx={{ display: "flex", alignItems: "center" }}>
				<Box sx={{ my: 4, mx: 4, display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
					<Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
						<LockResetIcon />
					</Avatar>
					<Typography component="h1" variant="h5">
						Set a new password
					</Typography>
					{done ? (
						<Box sx={{ mt: 3, maxWidth: 400, textAlign: "center" }}>
							<Typography variant="body1" sx={{ mb: 2 }}>
								Your password has been updated. You can now sign in with your new password.
							</Typography>
							<Button variant="contained" onClick={() => navigate("/login")}>
								Go to sign in
							</Button>
						</Box>
					) : (
						<Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1, maxWidth: 400 }}>
							<TextField margin="normal" required fullWidth name="password" label="New password" type="password" id="password" autoComplete="new-password" autoFocus />
							<TextField margin="normal" required fullWidth name="confirm" label="Confirm new password" type="password" id="confirm" autoComplete="new-password" />
							<Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 3, mb: 2 }}>
								{loading ? "Saving..." : "Update password"}
							</Button>
							<Box sx={{ textAlign: "center" }}>
								<Link component="button" type="button" variant="body2" onClick={() => navigate("/login")}>
									Back to sign in
								</Link>
							</Box>
							{error && (
								<Typography variant="body2" color="error" sx={{ mt: 2 }}>
									{error}
								</Typography>
							)}
						</Box>
					)}
				</Box>
			</Grid>
		</Grid>
	);
}
